import { useTheme } from "@mui/material";
import { ReportTypeEnum } from "@sinch/core/entities/serverEnums";
import { MdsSentimentDissatisfiedXs, MdsSentimentNeutralXs, MdsSentimentSatisfiedXs } from "@sinch/icons";
import { ReportTypeEnum as ReportType } from "@sinch/types/sinch.types";

import * as React from "react";
import { useTranslation } from "react-i18next";
import { useEntityProps } from "../utilsHooks";

/**
 * Single source of truth for how a feedback report type looks in the app: label, icon, color and text color.
 *
 * Use these getters wherever a feedback report type is shown.
 * Never hardcode its label, icon, color and text color in a component, change them here.
 * Every value is shown in the "Entity Identity/ReportType" story.
 */
export const useReportType = () => {
  const { t } = useTranslation();
  const theme = useTheme();

  const positionRoles = {
    [ReportTypeEnum.Good]: {
      label: t("ClosingForm.feedbackGood"),
      icon: MdsSentimentSatisfiedXs,
      color: theme.palette.success.main,
      textColor: theme.palette.success.main,
    },
    [ReportTypeEnum.Neutral]: {
      label: t("ClosingForm.feedbackNeutral"),
      icon: MdsSentimentNeutralXs,
      color: theme.palette.grey["800"],
      textColor: undefined,
    },
    [ReportTypeEnum.Bad]: {
      label: t("ClosingForm.feedbackBad"),
      icon: MdsSentimentDissatisfiedXs,
      color: theme.palette.error.main,
      textColor: theme.palette.custom.red["200"],
    },
  } as Record<
    ReportTypeEnum | ReportType,
    {
      label: string;
      icon: React.NamedExoticComponent<Omit<React.SVGProps<SVGSVGElement>, "ref"> & React.RefAttributes<SVGSVGElement>>;
      color: string;
      textColor: string | undefined;
    }
  >;
  const getProp = useEntityProps(positionRoles);

  const getReportTypeLabel = getProp<string>("label");
  const getReportTypeIcon =
    getProp<
      React.NamedExoticComponent<Omit<React.SVGProps<SVGSVGElement>, "ref"> & React.RefAttributes<SVGSVGElement>>
    >("icon");
  const getReportTypeColor = getProp<string>("color");
  const getReportTypeTextColor = getProp<string>("textColor");
  const sortedItems = [ReportTypeEnum.Good, ReportTypeEnum.Neutral, ReportTypeEnum.Bad];

  return { getReportTypeLabel, getReportTypeIcon, getReportTypeColor, getReportTypeTextColor, sortedItems };
};
